import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';

type Mode = 'login' | 'register';

interface FieldErrors {
  name?: string;
  email?: string;
  password?: string;
}

export default function Login() {
  const navigate = useNavigate();
  const { login, register } = useAuth();

  const [mode, setMode] = useState<Mode>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  function validate(): boolean {
    const e: FieldErrors = {};
    if (mode === 'register' && !name.trim()) {
      e.name = '请输入用户名';
    }
    if (!email.includes('@')) {
      e.email = '请输入有效的邮箱地址';
    }
    if (password.length < 6) {
      e.password = '密码至少 6 位';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setSubmitError('');
    if (!validate()) return;
    setSubmitting(true);
    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        await register(email, password, name.trim());
      }
      navigate('/', { replace: true });
    } catch (err) {
      setSubmitError((err as Error).message || '操作失败，请稍后重试。');
    } finally {
      setSubmitting(false);
    }
  }

  function switchMode(next: Mode) {
    setMode(next);
    setErrors({});
    setSubmitError('');
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-tabs">
          <button
            type="button"
            className={`auth-tab${mode === 'login' ? ' active' : ''}`}
            onClick={() => switchMode('login')}
          >
            登录
          </button>
          <button
            type="button"
            className={`auth-tab${mode === 'register' ? ' active' : ''}`}
            onClick={() => switchMode('register')}
          >
            注册
          </button>
        </div>

        <h1 className="auth-title">
          {mode === 'login' ? '欢迎回来' : '创建账户'}
        </h1>
        <p className="auth-subtitle">
          {mode === 'login'
            ? '登录以管理你的 Cloupak 应用'
            : '注册即可开始构建和部署应用'}
        </p>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          {mode === 'register' && (
            <div className="form-group">
              <label className="form-label" htmlFor="auth-name">
                用户名 <span className="required">*</span>
              </label>
              <input
                id="auth-name"
                className="form-input"
                type="text"
                placeholder="你的名字"
                value={name}
                maxLength={40}
                autoComplete="name"
                disabled={submitting}
                onChange={(e) => setName(e.target.value)}
              />
              {errors.name && <div className="field-error">{errors.name}</div>}
            </div>
          )}

          <div className="form-group">
            <label className="form-label" htmlFor="auth-email">
              邮箱 <span className="required">*</span>
            </label>
            <input
              id="auth-email"
              className="form-input"
              type="email"
              placeholder="you@example.com"
              value={email}
              autoComplete="email"
              disabled={submitting}
              onChange={(e) => setEmail(e.target.value)}
            />
            {errors.email && <div className="field-error">{errors.email}</div>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="auth-password">
              密码 <span className="required">*</span>
            </label>
            <input
              id="auth-password"
              className="form-input"
              type="password"
              placeholder="至少 6 位"
              value={password}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              disabled={submitting}
              onChange={(e) => setPassword(e.target.value)}
            />
            {errors.password && (
              <div className="field-error">{errors.password}</div>
            )}
          </div>

          {submitError && (
            <div className="state-box state-error">{submitError}</div>
          )}

          <button
            className="btn btn-primary btn-lg auth-submit"
            type="submit"
            disabled={submitting}
          >
            {submitting ? '处理中…' : mode === 'login' ? '登录' : '注册'}
          </button>
        </form>
      </div>
    </div>
  );
}
